// src/components/GamePanels.tsx (REFACTORED - Vertically Stacked Panels)
import React, { useState, useEffect } from "react";
import { Piece } from "../types/Board.ts";
import { Move } from "../utils/MoveHistoryManager.ts";
import ChatPanel from "./ChatPanel.tsx";

// Type imports
import type { GamePanelsProps } from "../types/GamePanels.ts";

// Panel components
import { MoveHistoryPanel } from "./panels/MoveHistoryPanel.tsx";
import { GameSettingsPanel } from "./panels/GameSettingsPanel.tsx";
import { CapturedPiecesPanel } from "./panels/CapturedPiecesPanel.tsx";

// Modal components
import {
  FiftyMoveWarningModal,
  ClockWarningModal,
  TimeoutModal,
  PromotionDialog,
} from "./modals/GameModals.tsx";

// Main GamePanels Component with All Display Panels + CHAT
const GamePanels: React.FC<GamePanelsProps> = ({
  boardRows,
  currentGameId,
  availablePromotionPieces,
  fiftyMoveLimit,
  seventyFiveMoveLimit,
  setFiftyMoveLimit,
  setSeventyFiveMoveLimit,
  diagonalCaptureOnly,
  setDiagonalCaptureOnly,
  showOpponentMoves,
  setShowOpponentMoves,
  clockEnabled,
  clockDuration,
  whiteTimeLeft,
  blackTimeLeft,
  clockRunning,
  clockStarted,
  currentTurn,
  toggleClock,
  handleClockDurationChange,
  startClock,
  resetClock,
  capturedPieces,
  materialAdvantage,
  capturedValues,
  moveHistory,
  gameStatus,
  historyPosition,
  isReviewingHistory,
  halfmoveClock,
  goBackward,
  goForward,
  goToCurrentPosition,
  claim50MoveDraw,
  show50MoveWarning,
  setShow50MoveWarning,
  showClockWarning,
  setShowClockWarning,
  showTimeoutModal,
  setShowTimeoutModal,
  timeoutResult,
  showPromotionDialog,
  setShowPromotionDialog,
  promotionMove,
  getPieceAt,
  handlePromotion,
  pieces,
  onLoadMoveHistory,

  // Chat props with defaults
  gameMode = "local",
  chatMessages = [],
  onSendChatMessage,
  currentUsername,
  opponentUsername,
  chatConnected = false,
}) => {
  return (
    <>
      {/* Extracted Modals */}
      
      {/* 50-Move Rule Warning Modal */}
      <FiftyMoveWarningModal
        show={show50MoveWarning}
        fiftyMoveLimit={fiftyMoveLimit}
        seventyFiveMoveLimit={seventyFiveMoveLimit}
        halfmoveClock={halfmoveClock}
        onClaimDraw={claim50MoveDraw}
        onContinue={() => setShow50MoveWarning(false)}
      />

      {/* Clock Warning Modal */}
      <ClockWarningModal
        show={showClockWarning}
        onStartClock={() => {
          startClock();
          setShowClockWarning(false);
        }}
        onContinue={() => setShowClockWarning(false)}
      />

      {/* Timeout Modal */}
      <TimeoutModal
        show={showTimeoutModal}
        timeoutResult={timeoutResult}
        boardRows={boardRows}
        onClose={() => setShowTimeoutModal(false)}
      />

      {/* Promotion Dialog */}
      <PromotionDialog
        show={showPromotionDialog}
        availablePromotionPieces={availablePromotionPieces}
        promotionMove={promotionMove}
        boardRows={boardRows}
        pieces={pieces}
        getPieceAt={getPieceAt}
        onPromotion={handlePromotion}
      />

      {/* Right Side Panels Container */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "5px",
          width: "320px",
          minWidth: "320px",
          maxWidth: "320px",
          flexShrink: 0,
        }}
      >
        {/* Combined Game Settings Panel (Promotion + Move Rules + Clock) */}
        <GameSettingsPanel
          availablePromotionPieces={availablePromotionPieces}
          boardRows={boardRows}
          pieces={pieces}
          fiftyMoveLimit={fiftyMoveLimit}
          seventyFiveMoveLimit={seventyFiveMoveLimit}
          halfmoveClock={halfmoveClock}
          setFiftyMoveLimit={setFiftyMoveLimit}
          setSeventyFiveMoveLimit={setSeventyFiveMoveLimit}
          diagonalCaptureOnly={diagonalCaptureOnly}
          setDiagonalCaptureOnly={setDiagonalCaptureOnly}
          showOpponentMoves={showOpponentMoves}
          setShowOpponentMoves={setShowOpponentMoves}
          clockEnabled={clockEnabled}
          clockDuration={clockDuration}
          whiteTimeLeft={whiteTimeLeft}
          blackTimeLeft={blackTimeLeft}
          clockRunning={clockRunning}
          clockStarted={clockStarted}
          currentTurn={currentTurn}
          toggleClock={toggleClock}
          handleClockDurationChange={handleClockDurationChange}
          startClock={startClock}
          resetClock={resetClock}
          gameMode={gameMode}
        />

        {/* Captured Pieces Panel - Always Visible */}
        <div
          style={{
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            border: "2px solid #333",
            padding: "10px",
          }}
        >
          <CapturedPiecesPanel
            capturedPieces={capturedPieces}
            materialAdvantage={materialAdvantage}
            capturedValues={capturedValues}
          />
        </div>

        {/* Chat Panel - Always Visible in Online Mode */}
        {gameMode === "online" && (
          <ChatPanel
            messages={chatMessages}
            onSendMessage={onSendChatMessage || (() => {})}
            currentUsername={currentUsername}
            opponentUsername={opponentUsername}
            connected={chatConnected}
          />
        )}
      </div>
    </>
  );
};

export default GamePanels;

// Re-export components for backward compatibility
export { MoveHistoryPanel } from "./panels/MoveHistoryPanel.tsx";
export { SaveHistoryDialog, LoadHistoryDialog } from "./modals/MoveHistoryModals.tsx";